import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavBar from "@/components/NavBar";
import api, { formatApiError } from "@/lib/api";
import { TASK_WIZARD } from "@/constants/testIds";
import {
  ArrowRight, ArrowLeft, Heart, ShoppingBag, FileCheck2, Camera, Wrench, Check,
  ShoppingCart, PackageOpen, Eye, ClipboardCheck,
} from "lucide-react";

const ICONS = {
  check_in: Heart,
  purchase_delivery: ShoppingBag,
  document_collection: FileCheck2,
  verification: Camera,
  hire_pro: Wrench,
};

// What the agent will actually do — drives whether we need an item cost & whether
// Pay2hold (project-style escrow) is offered at funding time.
const PURCHASE_TYPES = [
  { value: "buy_items", label: "Buy items for me", hint: "Agent purchases on your behalf (gift, groceries, etc.).", icon: ShoppingCart, needsBudget: true },
  { value: "pickup_delivery", label: "Pick up & deliver", hint: "Item already paid for / no purchase needed — just collect & deliver.", icon: PackageOpen, needsBudget: false },
  { value: "oversee_project", label: "Oversee / supervise a project", hint: "Attend, monitor a builder, inspect work, attend a meeting on your behalf.", icon: Eye, needsBudget: false },
  { value: "checkin_verification", label: "Check-in / verification visit", hint: "Welfare check on family or property — proof via photos.", icon: ClipboardCheck, needsBudget: false },
];

// Sensible default when the user picks a category
const DEFAULT_PURCHASE_TYPE = {
  purchase_delivery: "buy_items",
  document_collection: "pickup_delivery",
  hire_pro: "oversee_project",
  check_in: "checkin_verification",
  verification: "checkin_verification",
};

const CITIES = ["Lagos", "Abuja", "Port-Harcourt", "Edo (Benin City)", "Delta (Warri/Asaba)"];

export default function NewTask() {
  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState({});
  const [form, setForm] = useState({
    category: "",
    title: "",
    description: "",
    city: "Lagos",
    address: "",
    budget_ngn: 0,
    deadline: "",
    special_instructions: "",
    recipient_name: "",
    recipient_phone: "",
    purchase_type: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/tasks/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const setVal = (k, v) => setForm({ ...form, [k]: v });

  const pickCategory = (key) => {
    setForm((f) => ({
      ...f,
      category: key,
      // Pre-select purchase type whenever the user changes category — they can override
      purchase_type: DEFAULT_PURCHASE_TYPE[key] || "pickup_delivery",
    }));
  };

  const activePurchase = PURCHASE_TYPES.find((p) => p.value === form.purchase_type);
  const needsBudget = !!activePurchase?.needsBudget;

  const canNext =
    (step === 0 && !!form.category) ||
    (step === 1 && form.title.trim().length >= 3 && form.description.trim().length >= 5 && !!form.city && !!form.purchase_type) ||
    step === 2;

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = {
        ...form,
        // Only buy_items tasks send an item cost — everything else is service-only
        budget_ngn: needsBudget ? (Number(form.budget_ngn) || 0) : 0,
        purchase_type: form.purchase_type || undefined,
      };
      if (payload.deadline) payload.deadline = new Date(payload.deadline).toISOString();
      const { data } = await api.post("/tasks", payload);
      nav(`/tasks/${data.task_id}`);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen">
      <NavBar />
      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10 grid lg:grid-cols-12 gap-10">
        {/* Left rail: sticky progress */}
        <aside className="lg:col-span-4">
          <div className="lg:sticky lg:top-24">
            <span className="eb-chip">New Task · Step {step + 1}/3</span>
            <h1 className="mt-4 text-4xl md:text-5xl leading-tight">
              {step === 0 && "Pick a service."}
              {step === 1 && "Tell us the details."}
              {step === 2 && "Review & confirm."}
            </h1>
            <p className="mt-3 text-[var(--eb-text-dim)] max-w-md">
              {step === 0 && "Choose the type of errand. Each has a transparent service fee."}
              {step === 1 && "Be specific — the more detail, the better the result. Photos and constraints help."}
              {step === 2 && "We'll create your task and ask you to fund the escrow next."}
            </p>

            <div className="mt-8 space-y-3">
              {["Service", "Details", "Review"].map((label, i) => (
                <div key={label} className={`flex items-center gap-3 ${i === step ? "" : "opacity-60"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-[800] ${i < step ? "bg-[var(--eb-primary)] text-white" : i === step ? "border-2 border-[var(--eb-primary)] text-[var(--eb-primary)]" : "border border-[var(--eb-border)] text-[var(--eb-text-dim)]"}`}>
                    {i < step ? <Check className="w-4 h-4" /> : i + 1}
                  </div>
                  <span className={i === step ? "font-[700]" : "text-[var(--eb-text-dim)]"}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </aside>

        {/* Right: form */}
        <section className="lg:col-span-8">
          {step === 0 && (
            <div className="grid sm:grid-cols-2 gap-4">
              {Object.entries(categories).map(([key, c]) => {
                const Icon = ICONS[key] || ShoppingBag;
                const active = form.category === key;
                return (
                  <button
                    key={key}
                    data-testid={`${TASK_WIZARD.catBtn}-${key}`}
                    onClick={() => pickCategory(key)}
                    className={`eb-card text-left p-6 transition-all hover:-translate-y-1 ${active ? "border-[var(--eb-primary)] ring-2 ring-[var(--eb-primary)]/30" : ""}`}
                  >
                    <Icon className={`w-7 h-7 ${active ? "text-[var(--eb-primary)]" : "text-[var(--eb-text-dim)]"}`} />
                    <div className="mt-4 text-xl font-[700]">{c.label}</div>
                    <div className="mt-1 text-xs text-[var(--eb-text-dim)]">
                      Service fee: <span className="text-[var(--eb-primary)]">₦{c.service_fee_ngn?.toLocaleString()}</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          {step === 1 && (
            <div className="eb-card p-7 space-y-4">
              <div>
                <label className="eb-label">What kind of task is this?</label>
                <div data-testid={TASK_WIZARD.purchaseType} className="grid sm:grid-cols-2 gap-2">
                  {PURCHASE_TYPES.map((p) => {
                    const PIcon = p.icon;
                    const active = form.purchase_type === p.value;
                    return (
                      <button
                        key={p.value}
                        type="button"
                        data-testid={`${TASK_WIZARD.purchaseType}-${p.value}`}
                        onClick={() => setVal("purchase_type", p.value)}
                        className={`text-left p-3 rounded-xl border transition-all ${active ? "border-[var(--eb-primary)] bg-[rgba(14,122,61,0.06)]" : "border-[var(--eb-border)] hover:border-[var(--eb-primary)]/40"}`}
                      >
                        <div className="flex items-center gap-2">
                          <PIcon className={`w-4 h-4 ${active ? "text-[var(--eb-primary)]" : "text-[var(--eb-text-dim)]"}`} />
                          <div className="font-[700] text-sm">{p.label}</div>
                        </div>
                        <div className="mt-1 text-[11px] text-[var(--eb-text-dim)] leading-snug">{p.hint}</div>
                      </button>
                    );
                  })}
                </div>
                {!needsBudget && form.purchase_type && (
                  <div className="mt-2 text-[11px] text-[var(--eb-text-dim)]">
                    Service-only tasks are eligible for <span className="font-[700] text-[var(--eb-text)]">Pay2Hold</span> escrow at checkout.
                  </div>
                )}
              </div>

              <div>
                <label className="eb-label">Title</label>
                <input data-testid={TASK_WIZARD.title} value={form.title} onChange={set("title")} className="eb-input"
                  placeholder={form.category === "purchase_delivery" ? "iPhone 15 Pro — birthday gift" : "Sunday check-in at mum's place"} />
              </div>
              <div>
                <label className="eb-label">Description</label>
                <textarea data-testid={TASK_WIZARD.desc} value={form.description} onChange={set("description")} rows="4" className="eb-input"
                  placeholder="What exactly needs to happen? Include constraints, store preferences, etc." />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eb-label">City</label>
                  <select data-testid={TASK_WIZARD.city} value={form.city} onChange={set("city")} className="eb-input">
                    {CITIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="eb-label">Deadline (optional)</label>
                  <input data-testid={TASK_WIZARD.deadline} type="datetime-local" value={form.deadline} onChange={set("deadline")} className="eb-input" />
                </div>
              </div>
              <div>
                <label className="eb-label">Address / Location</label>
                <input data-testid={TASK_WIZARD.address} value={form.address} onChange={set("address")} className="eb-input" placeholder="Maitama, Abuja — wife's office, 14 Aminu Kano Crescent" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {needsBudget ? (
                  <div>
                    <label className="eb-label">Item cost (₦)</label>
                    <input data-testid={TASK_WIZARD.budget} type="number" min="0" value={form.budget_ngn} onChange={set("budget_ngn")} className="eb-input" placeholder="e.g. 1200000" />
                    <div className="mt-1 text-[11px] text-[var(--eb-text-dim)]">
                      What we&apos;ll spend on your behalf (gift, groceries, etc.).
                    </div>
                  </div>
                ) : (
                  <div className="rounded-xl border border-dashed border-[var(--eb-border)] p-3 bg-[var(--eb-surface)]/40">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--eb-text-dim)]">No item purchase</div>
                    <div className="mt-1 text-xs text-[var(--eb-text)]">
                      You&apos;ll only fund the agent service fee — Pay2Hold available.
                    </div>
                  </div>
                )}
                <div>
                  <label className="eb-label">Recipient name (optional)</label>
                  <input data-testid={TASK_WIZARD.recipientName} value={form.recipient_name} onChange={set("recipient_name")} className="eb-input" placeholder="Adaeze Obi" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="eb-label">Recipient phone (optional)</label>
                  <input data-testid={TASK_WIZARD.recipientPhone} value={form.recipient_phone} onChange={set("recipient_phone")} className="eb-input" placeholder="+234…" />
                </div>
              </div>
              <div>
                <label className="eb-label">Special instructions</label>
                <textarea data-testid={TASK_WIZARD.instructions} value={form.special_instructions} onChange={set("special_instructions")} rows="3" className="eb-input"
                  placeholder='Gift-wrap, handwritten note: "To my sunshine — Happy birthday! Love, Emeka"' />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="eb-card p-7">
              <div className="text-[var(--eb-text-dim)] text-xs uppercase tracking-[0.2em]">Review</div>
              <h3 className="text-2xl mt-2 font-[800]">{form.title || "Untitled task"}</h3>
              <div className="text-[var(--eb-text-dim)] mt-1">{categories[form.category]?.label} · {form.city}</div>
              <p className="mt-4 text-[var(--eb-text)]">{form.description}</p>
              <dl className="mt-6 grid sm:grid-cols-2 gap-4 text-sm">
                <Row label="Task type" value={activePurchase?.label || "—"} />
                <Row label="Address" value={form.address || "—"} />
                {needsBudget && (
                  <Row label="Item cost" value={form.budget_ngn ? `₦${Number(form.budget_ngn).toLocaleString()}` : "—"} />
                )}
                <Row label="Deadline" value={form.deadline ? new Date(form.deadline).toLocaleString() : "—"} />
                <Row label="Recipient" value={form.recipient_name ? `${form.recipient_name} ${form.recipient_phone ? `(${form.recipient_phone})` : ""}` : "—"} />
                <Row label="Service fee" value={`₦${categories[form.category]?.service_fee_ngn?.toLocaleString() || "—"}`} />
                <Row label="Total to fund" value={(() => {
                  const sf = Number(categories[form.category]?.service_fee_ngn || 0);
                  const ic = needsBudget ? Number(form.budget_ngn || 0) : 0;
                  return `₦${(sf + ic).toLocaleString()}`;
                })()} />
                <Row label="Instructions" value={form.special_instructions || "—"} full />
              </dl>
              <div className="mt-6 text-xs text-[var(--eb-text-dim)] border-t border-[var(--eb-border)] pt-4">
                {needsBudget
                  ? "After creating, you'll fund the escrow in one click — service fee + item cost together. Once held, an agent will claim the task."
                  : "After creating, you'll fund just the agent service fee — service-only tasks are eligible for Pay2Hold escrow."}
              </div>
            </div>
          )}

          {err && <div className="mt-4 text-sm text-[var(--eb-secondary)]">{err}</div>}

          <div className="mt-6 flex justify-between gap-3">
            <button
              data-testid={TASK_WIZARD.back}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="eb-btn eb-btn-ghost disabled:opacity-30"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            {step < 2 ? (
              <button
                data-testid={TASK_WIZARD.next}
                onClick={() => canNext && setStep((s) => s + 1)}
                disabled={!canNext}
                className="eb-btn eb-btn-primary disabled:opacity-50"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                data-testid={TASK_WIZARD.submit}
                onClick={submit}
                disabled={busy}
                className="eb-btn eb-btn-primary"
              >
                {busy ? "Creating…" : "Create task"} <ArrowRight className="w-4 h-4" />
              </button>
            )}
          </div>
        </section>
      </main>
    </div>
  );
}

function Row({ label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--eb-text-dim)]">{label}</div>
      <div className="mt-1 text-[var(--eb-text)]">{value}</div>
    </div>
  );
}
